import NiceModal from '@ebay/nice-modal-react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import Button from 'components/field/Button';
import TextArea from 'components/field/TextArea';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useRefreshToken from 'hooks/useRefreshToken';

import { AI_ASSISTANT } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faClockRotateLeft, faCopy, faEdit, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUp, faChartLine, faCode, faWrench } from '@fortawesome/pro-solid-svg-icons';

import * as S from './AIAssistantChat.styled';
import ChatsHistory from './ChatsHistory';

const PREDEFINED_QUESTIONS = [
	{
		question: 'predefinedQuestion1',
		description: 'predefinedQuestion1Description',
		icon: faCode,
		iconColor: 'var(--content-5)',
		size: 'large'
	},
	{
		question: 'predefinedQuestion2',
		description: 'predefinedQuestion2Description',
		icon: faWrench,
		iconColor: 'var(--fa-regular-loader-circle-bolt-color)',
		size: 'medium'
	},
	{
		question: 'predefinedQuestion3',
		description: 'predefinedQuestion3Description',
		icon: faChartLine,
		iconColor: 'var(--forest)',
		size: 'medium'
	}
];

const generateUniqueId = () => {
	return `chat-${Date.now().toString(36)}-${crypto.randomUUID()}`;
};

const AIAssistantChat = NiceModal.create(({ modalName }) => {
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);

	const [searchValue, setSearchValue] = useState('');
	const [chatId, setChatId] = useState(generateUniqueId());
	const [messages, setMessages] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isHistoryChatLoading, setIsHistoryChatLoading] = useState(false);
	const [isAwaitingAIResponse, setIsAwaitingAIResponse] = useState(false);
	const { t } = useTranslation();
	const { showInfo } = useAlert();
	const refreshAccess = useRefreshToken();

	const chatWindowRef = useRef(null);

	useEffect(() => {
		if (chatWindowRef.current) {
			chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
		}
	}, [messages]);

	const handleSearch = async () => {
		if (!searchValue || isAwaitingAIResponse) return;
		setMessages((prevMessages) => [
			...prevMessages,
			{ sender: 'User', text: searchValue, uuid: generateUniqueId() }
		]);
		setSearchValue('');
		setIsLoading(true);
		setIsAwaitingAIResponse(true);
		let token = localStorage.getItem('ACCESS_TOKEN');
		let triedRefresh = false;
		const fetchWithToken = async (tokenToUse) => {
			try {
				await fetchEventSource(
					`${AI_ASSISTANT.ASK}?message=${encodeURIComponent(searchValue)}&sessionId=${chatId}`,
					{
						method: 'GET',
						headers: {
							'x-forwarded-authorization': `Bearer ${tokenToUse}`
						},
						onopen: async (res) => {
							if (!triedRefresh && (res?.status === 401 || res?.status === 403)) {
								triedRefresh = true;
								const newToken = await refreshAccess();
								if (newToken) {
									localStorage.setItem('ACCESS_TOKEN', newToken);
									token = newToken;
									return fetchWithToken(newToken);
								}
							} else if (res.status !== 200) {
								setIsLoading(false);
								setIsAwaitingAIResponse(false);
								setMessages((prevMessages) => [
									...prevMessages,
									{
										sender: 'AI',
										text: t('errorOccuredWhileTalkingToAI'),
										uuid: generateUniqueId()
									}
								]);
								return;
							}
						},
						onmessage: (event) => {
							try {
								const parsedEvent = JSON.parse(event.data);
								if (parsedEvent.close) {
									setIsLoading(false);
									setIsAwaitingAIResponse(false);
									return;
								}
								if (parsedEvent?.textResponse) {
									setMessages((prevMessages) => [
										...prevMessages,
										{
											sender: 'AI',
											text: parsedEvent.textResponse,
											uuid: parsedEvent.uuid
										}
									]);
								}
							} catch (error) {
								AppLogger.error(error);
								setMessages((prevMessages) => [
									...prevMessages,
									{
										sender: 'AI',
										text: t('errorWhileProcessingTheResponse'),
										uuid: generateUniqueId()
									}
								]);
								setIsAwaitingAIResponse(false);
							} finally {
								setIsLoading(false);
							}
						},
						onerror: async (error) => {
							AppLogger.error(error);
							setMessages((prevMessages) => [
								...prevMessages,
								{
									sender: 'AI',
									text: t(error.response?.data?.code || 'errorOccuredWhileTalkingToAI'),
									uuid: generateUniqueId()
								}
							]);
							setIsLoading(false);
							setIsAwaitingAIResponse(false);
						}
					}
				);
			} catch (error) {
				AppLogger.error(error);
				setMessages((prevMessages) => [
					...prevMessages,
					{
						sender: 'AI',
						text: t(error.response?.data?.code || 'unexpectedError'),
						uuid: generateUniqueId()
					}
				]);
				setIsLoading(false);
				setIsAwaitingAIResponse(false);
			}
		};
		await fetchWithToken(token);
	};

	return (
		<S.ModalWrapper position="right" fullwidth maxWidth={isHistoryOpen ? 'large' : 'x-small'} modalName={modalName}>
			<S.ModalContentVariation>
				<S.ModalHeader>
					<Typography variant="p2" fontWeight="500">
						chatWithOtoAiAssistant
					</Typography>
				</S.ModalHeader>
				<S.TopButtons>
					<Button
						iconButton={!isHistoryOpen}
						icon={isHistoryOpen ? faEyeSlash : faClockRotateLeft}
						onClick={() => setIsHistoryOpen(!isHistoryOpen)}
						label="hideHistory"
						variant="borderless"
					/>
					{messages.length > 0 && (
						<Button
							iconButton
							icon={faEdit}
							onClick={() => {
								setIsHistoryOpen(false);
								setChatId(generateUniqueId());
								setMessages([]);
							}}
							variant="borderless"
						/>
					)}
				</S.TopButtons>
				<S.ModalContentWrapper>
					{messages.length === 0 && !isHistoryChatLoading ? (
						<EmptyChatState
							searchValue={searchValue}
							setSearchValue={setSearchValue}
							handleSearch={handleSearch}
							isHistoryOpen={isHistoryOpen}
							isAwaitingAIResponse={isAwaitingAIResponse}
						/>
					) : (
						<S.ChatWrapper isHistoryOpen={isHistoryOpen}>
							{isHistoryChatLoading ? (
								<Typography variant="p2">loading</Typography>
							) : (
								messages.length > 0 && (
									<S.ChatWindow ref={chatWindowRef}>
										{Object.values(
											messages.reduce((acc, message) => {
												if (!message.uuid) {
													message.uuid = generateUniqueId();
												}
												if (acc[message.uuid]) {
													acc[message.uuid].text += message.text;
												} else {
													acc[message.uuid] = { ...message };
												}
												return acc;
											}, {})
										).map((message, index) => (
											<S.Message key={index} sender={message.sender}>
												{message.sender === 'AI' ? (
													<S.AgentResponse>
														<ReactMarkdown
															components={{
																pre: ({ _, ...props }) => (
																	<S.CodeBlock {...props} className="language-jsx">
																		<S.CodeBlockHeader>
																			<Button
																				iconButton
																				variant="borderless"
																				icon={faCopy}
																				onClick={() => {
																					showInfo({
																						header: 'copyComponentHeader',
																						message: 'copyComponentMessage'
																					});
																					navigator.clipboard.writeText(
																						props.children.props.children
																					);
																				}}
																			/>
																		</S.CodeBlockHeader>
																		<S.CodeBlockContent>
																			{props.children}
																		</S.CodeBlockContent>
																	</S.CodeBlock>
																)
															}}>
															{message.text}
														</ReactMarkdown>
													</S.AgentResponse>
												) : (
													<S.MessageBubble>
														<Typography noWrap>{message.text}</Typography>
													</S.MessageBubble>
												)}
											</S.Message>
										))}
										{isLoading && (
											<S.AgentResponseLoading>
												<Image
													width="3rem"
													height="3rem"
													url="https://storage.googleapis.com/tryoto-public/public/assets/images/OTO-AI/oto-ai-logo.png"
													alt="AI Assistant"
												/>
												<Typography color="var(--content-3)">thinking</Typography>
											</S.AgentResponseLoading>
										)}
									</S.ChatWindow>
								)
							)}
							<S.SearchInputWrapper>
								<TextArea
									value={searchValue}
									setValue={setSearchValue}
									placeholder={'aiSearchPlaceholder'}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											if (!isAwaitingAIResponse && searchValue?.trim()) handleSearch();
										}
									}}
									className="ai-search-input"
									maxLength={4096}
								/>
								<S.SearchInputButton
									iconButton
									icon={faArrowUp}
									variant="black"
									disabled={!searchValue || isAwaitingAIResponse}
									onClick={!isAwaitingAIResponse ? handleSearch : undefined}
								/>
							</S.SearchInputWrapper>
						</S.ChatWrapper>
					)}
					{isHistoryOpen && (
						<ChatsHistory
							setChatId={setChatId}
							setMessages={setMessages}
							setIsLoading={setIsHistoryChatLoading}
						/>
					)}
				</S.ModalContentWrapper>
			</S.ModalContentVariation>
		</S.ModalWrapper>
	);
});

const EmptyChatState = ({ searchValue, setSearchValue, handleSearch, isHistoryOpen, isAwaitingAIResponse }) => {
	const { t } = useTranslation();
	return (
		<S.ChatWrapper className="empty" isHistoryOpen={isHistoryOpen}>
			<S.EmptyStateWrapper>
				<Image
					width="7rem"
					height="7rem"
					url="https://storage.googleapis.com/tryoto-public/public/assets/images/OTO-AI/oto-ai-logo.png"
					alt="AI Assistant"
					className="mb-5"
				/>
				<S.TextWrapper>
					<Typography variant="p1" fontWeight="700" gutterBottom>
						welcomeToAIAssistant
					</Typography>
					<Typography variant="p2" fontWeight="500" color="var(--content-2)" noWrap>
						aiAssistantSubtitle
					</Typography>
				</S.TextWrapper>
				<div className="px-5">
					<S.EmptyStateSearchInputWrapper>
						<TextArea
							value={searchValue}
							setValue={setSearchValue}
							placeholder={'aiSearchPlaceholder'}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									if (!isAwaitingAIResponse && searchValue?.trim()) handleSearch();
								}
							}}
							className="ai-search-input"
							maxLength={4096}
						/>
						<S.SearchInputButton
							iconButton
							icon={faArrowUp}
							variant="black"
							disabled={!searchValue || isAwaitingAIResponse}
							onClick={!isAwaitingAIResponse ? handleSearch : undefined}
						/>
					</S.EmptyStateSearchInputWrapper>
					<S.ExamplesWrapper>
						<Typography variant="p3" fontWeight="700" gutterBottom>
							tryTheseExamples
						</Typography>
						<S.PredefinedQuestions>
							{PREDEFINED_QUESTIONS.map((item, index) => (
								<S.PredefinedQuestionBox
									key={index}
									onClick={() => {
										setSearchValue(t(item.question));
									}}
									size={item.size}>
									<S.QuestionHeader>
										<Icon icon={item.icon} color={item.iconColor} size="medium" />
										<Typography variant="p2" fontWeight="700" noWrap>
											{item.question}
										</Typography>
									</S.QuestionHeader>
									{item.description && (
										<Typography variant="caption" fontWeight="500" color="var(--content-3)" noWrap>
											{item.description}
										</Typography>
									)}
								</S.PredefinedQuestionBox>
							))}
						</S.PredefinedQuestions>
					</S.ExamplesWrapper>
				</div>
			</S.EmptyStateWrapper>
		</S.ChatWrapper>
	);
};

export default AIAssistantChat;
